import React from "react";
import { ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import ReportLayout from "../components/ReportLayout";
import InitiativeSpotlight from "../components/InitiativeSpotlight";
import {
    Meta,
    Stat,
    Person,
    StatusPill,
    CrossFunctional,
} from "../components/ReportPrimitives";

// Edit this when you publish a new version of the Operations report.
// Each report has its own LAST_UPDATED constant — they update independently.
const LAST_UPDATED = "May 20, 2026";

/* -------------------- DATA -------------------- */

const NAV_ITEMS = [
    { href: "#summary", label: "Summary" },
    { href: "#workstreams", label: "Workstreams" },
    { href: "#priorities", label: "Forward" },
    { href: "#kpis", label: "KPIs" },
    { href: "#capacity", label: "Capacity" },
    { href: "#dependencies", label: "Dependencies" },
    { href: "#risks", label: "Risks" },
];

/* 8 Workstreams — top deliverables per stream (kept tight for 5-page PDF). */
const WORKSTREAMS = [
    {
        n: "01",
        name: "Organization & Governance",
        items: [
            "Define COO TOM (target operating model)",
            "Implement Operations governance for CC roll-out markets",
            "Cascade COO KPI targets to FTE level",
            "Establish governance cadence (weekly ops review, SLA, escalation)",
        ],
    },
    {
        n: "02",
        name: "Operations Processes",
        items: [
            "Map as-is COO processes for CC and non-CC customers (Croatia)",
            "Define target processes incl. ownership for International TOM",
            "Define Adyen migration process with CRO and CTO",
            "Operational playbooks by market",
        ],
    },
    {
        n: "03",
        name: "BO Admin",
        items: [
            "Assess current Data Entry & POS Operations structures",
            "Roll out new digital onboarding processes (per CTO delivery)",
            "Map to-be digital onboarding flows with CRO and CTO",
            "Support new markets per agreed processes",
        ],
    },
    {
        n: "04",
        name: "Logistics",
        items: [
            "Map current inventory & POS terminal flows",
            "Define target inventory management model",
            "Regional logistics setup for key markets",
            "Asset audit & reconciliation process with CFO",
        ],
    },
    {
        n: "05",
        name: "Customer Support",
        items: [
            "Assess Support structure & ticket flows",
            "Define escalation workflows & SLAs",
            "Business continuity / incident recovery process",
        ],
    },
    {
        n: "06",
        name: "Service Operations",
        items: [
            "Standardize Support workflows",
            "Implement incident review process",
            "Support agents cookbook (SOPs)",
        ],
    },
    {
        n: "07",
        name: "Tools & Automation",
        items: [
            "Audit operational tools landscape",
            "Phase 1 tooling improvements aligned with CRO/CTO",
            "AI quick wins & ticketing/helpdesk MVP",
            "Internal knowledge base structure",
        ],
    },
    {
        n: "08",
        name: "Reporting & Performance",
        items: [
            "MVP reporting requirements for E2E processes",
            "Operational KPI tree & scorecard",
            "Executive COO dashboard",
            "Country-by-country operational reporting",
        ],
    },
];

/* Forward plan — May / June / July 2026 (3 phase buckets). */
const FORWARD_PLAN = [
    {
        bucket: "May 2026",
        tag: "Foundation",
        items: [
            { name: "TOM definition & governance setup", owner: "Tanja" },
            { name: "As-is process mapping (Croatia)", owner: "Igor Pribek" },
            { name: "COO KPI alignment with CRO/CTO/CFO", owner: "Tanja" },
            { name: "Tools landscape audit", owner: "Tanja + Igor Pribek" },
        ],
    },
    {
        bucket: "June 2026",
        tag: "Activation",
        items: [
            { name: "Adyen process definition", owner: "Tanja + CTO/CRO" },
            { name: "Take over Data Entry & POS Ops teams", owner: "Igor Pribek" },
            { name: "Tooling MVP + AI quick wins", owner: "Tanja + Igor Pribek" },
            { name: "Governance cadence live", owner: "Tanja" },
        ],
    },
    {
        bucket: "July 2026",
        tag: "Pilot & Scale",
        items: [
            { name: "Pilot international operating model", owner: "Tanja" },
            { name: "Escalation matrix & support SLAs", owner: "Igor Pribek" },
            { name: "Logistics partner contracts (key markets)", owner: "Tanja" },
            { name: "COO operational scorecard launch", owner: "Tanja" },
            { name: "Country-by-country reporting baseline", owner: "Tanja + Igor Pribek" },
            { name: "Executive COO dashboard live", owner: "Tanja" },
        ],
    },
];

/* Cross-team dependencies — heaviest section in this report.
   `link` (optional) jumps to the relevant section of the sister report. */
const DEPENDENCIES = [
    {
        team: "Product & IT",
        owner: "CTO / CRO",
        weight: "Highest",
        link: { href: "/product-it#progress", label: "View Product & IT report" },
        items: [
            "Digital onboarding rollout — gates on CTO delivery",
            "Adyen migration process definition",
            "Tooling roadmap, AI quick wins, automation backlog",
            "Operational metrics from IT/Data Officer",
        ],
    },
    {
        team: "Finance",
        owner: "CFO",
        weight: "High",
        items: [
            "Asset & inventory governance transition to CFO process",
            "Asset audit & reconciliation",
            "Sales → Ops → Finance handoff mapping",
        ],
    },
    {
        team: "Business / Commercial",
        owner: "CBO + Country Leads",
        weight: "High",
        link: { href: "/business#priorities", label: "View Business report" },
        items: [
            "GTM activation alignment with Operations readiness",
            "New market launch governance & checklist",
            "Operations input into commercial onboarding",
        ],
    },
];

/* Risks — execution + dependency heavy. */
const RISKS = [
    "Heavy CTO dependency — most workstreams gate on Product & IT delivery; risk of bottleneck.",
    "Majority of workstreams in 'Not Started' — execution risk during stand-up phase.",
    "Lean ownership: 2 owners (Tanja + Igor Pribek) for 8 parallel workstreams.",
    "International expansion timeline depends on TOM lock-in by end of May.",
    "CFO governance transition for assets — coordination risk if not sequenced with logistics partner contracts.",
];

/* Teams to absorb (capacity narrative). */
const ABSORB_TEAMS = [
    "Data Entry team",
    "POS Operations team",
    "Support team",
    "Inventory team",
];

/* -------------------- COMPONENT -------------------- */

const OperationsReport = () => {
    return (
        <ReportLayout navItems={NAV_ITEMS} lastUpdated={LAST_UPDATED} reportType="operations">
            {/* HERO */}
            <section
                id="top"
                className="relative bg-black text-white overflow-hidden"
                data-testid="hero-section"
            >
                <div className="absolute inset-0 fora-pattern opacity-90" />
                <div className="absolute -top-32 -right-32 w-[520px] h-[520px] rounded-full bg-[var(--fora-red-1)] opacity-25 blur-3xl" />
                <div className="relative max-w-[1280px] mx-auto px-6 lg:px-10 pt-20 pb-24 lg:pt-28 lg:pb-32">
                    <div className="flex items-center gap-3 rise">
                        <span className="red-dot" />
                        <span className="section-num text-white/80">
                            Operations Progress · Confidential
                        </span>
                    </div>

                    <h1
                        className="rise d1 mt-6 font-[800] tracking-[-0.04em] leading-[0.95] text-4xl sm:text-6xl lg:text-[88px]"
                        data-testid="hero-title"
                    >
                        Operations
                        <br />
                        Build &amp; Setup.
                    </h1>

                    <p className="rise d2 mt-6 max-w-2xl text-sm sm:text-lg text-white/80 leading-relaxed">
                        Stand-up phase. Reporting period{" "}
                        <span className="text-white font-semibold">
                            May 2026
                        </span>
                        , forward view{" "}
                        <span className="text-white font-semibold">
                            May – July 2026
                        </span>
                        . Building the target operating model, governance, and
                        execution capabilities to support international
                        expansion and the Adyen integration.
                    </p>

                    <div className="rise d3 mt-12 grid grid-cols-2 md:grid-cols-4 gap-6 lg:gap-10 max-w-4xl">
                        <Meta
                            label="Submitted to"
                            value="Ivan Brkić"
                            sub="Managing Director"
                        />
                        <Meta
                            label="Reporting"
                            value="May 2026"
                            sub="Setup phase"
                        />
                        <Meta
                            label="Forward"
                            value="May – Jul 2026"
                            sub="3 phase buckets"
                        />
                        <Meta
                            label="Owner"
                            value="Tanja Krasić"
                            sub=""
                        />
                    </div>

                    <div className="rise d4 mt-12 flex flex-wrap items-center gap-3">
                        <span className="chip">
                            <span className="red-dot" /> COO · Tanja Krasić
                        </span>
                        <span className="chip">
                            <span className="red-dot" /> Igor Pribek · Operations
                        </span>
                    </div>
                </div>

                {/* Marquee strip */}
                <div className="fora-strip relative">
                    <div className="fora-strip-track py-3">
                        {[
                            "TOM DEFINITION",
                            "GOVERNANCE SETUP",
                            "ADYEN INTEGRATION",
                            "INTERNATIONAL EXPANSION",
                            "COO SCORECARD",
                            "8 WORKSTREAMS",
                            "OPERATIONS BUILD",
                        ]
                            .concat([
                                "TOM DEFINITION",
                                "GOVERNANCE SETUP",
                                "ADYEN INTEGRATION",
                                "INTERNATIONAL EXPANSION",
                                "COO SCORECARD",
                                "8 WORKSTREAMS",
                                "OPERATIONS BUILD",
                            ])
                            .map((s, i) => (
                                <React.Fragment key={i}>
                                    <span>{s}</span>
                                    <span className="pip" />
                                </React.Fragment>
                            ))}
                    </div>
                </div>
            </section>

            {/* CROSS-CUTTING INITIATIVE SPOTLIGHT */}
            <InitiativeSpotlight />

            {/* 01 — MANDATE & SUMMARY */}
            <section
                id="summary"
                className="bg-white"
                data-testid="summary-section"
            >
                <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-20 lg:py-28">
                    <div className="grid grid-cols-12 gap-8">
                        <div className="col-span-12 lg:col-span-4">
                            <div className="flex items-center gap-3">
                                <span className="red-dot" />
                                <span className="section-num">
                                    01 — Mandate
                                </span>
                            </div>
                            <h2 className="mt-5 font-[800] tracking-[-0.03em] leading-[1] text-4xl sm:text-5xl">
                                Mandate
                                <br />
                                &amp; scope.
                            </h2>
                        </div>
                        <div
                            className="col-span-12 lg:col-span-8"
                            data-testid="summary-body"
                        >
                            <div className="space-y-5 text-sm sm:text-base leading-relaxed text-[var(--fora-ink)]/85 break-words">
                                <p>
                                    The Operations function is in{" "}
                                    <strong>stand-up phase</strong> — building
                                    the{" "}
                                    <strong>target operating model</strong>{" "}
                                    (TOM), governance, and execution
                                    capabilities to support FORA's expansion
                                    into international markets and the{" "}
                                    <strong>Adyen integration</strong>.
                                </p>
                                <p>
                                    Eight workstreams advance in parallel
                                    across{" "}
                                    <strong>Organization &amp; Governance</strong>
                                    , <strong>Operations Processes</strong>,{" "}
                                    <strong>BO Admin</strong>,{" "}
                                    <strong>Logistics</strong>,{" "}
                                    <strong>Customer Support</strong>,{" "}
                                    <strong>Service</strong>,{" "}
                                    <strong>Tools &amp; Automation</strong>,
                                    and <strong>Reporting &amp; Performance</strong>.
                                </p>
                                <p>
                                    Operations has the{" "}
                                    <strong>highest cross-team dependency</strong>{" "}
                                    profile — heavy reliance on Product &amp; IT
                                    (CTO/CRO) for digital onboarding rollout,
                                    Adyen migration, and tooling, plus on
                                    Finance (CFO) for asset and inventory
                                    governance transition.
                                </p>
                                <p>
                                    May–July is the foundation period. By
                                    end of July, the function should have
                                    governance, processes, tooling, and KPIs
                                    in place to scale with international
                                    expansion.
                                </p>
                            </div>

                            <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-8 mb-8">
                                <Stat n="8" label="Workstreams" />
                                <Stat n="2" label="Owners" />
                                <Stat n="3" label="Phase buckets" />
                                <Stat n="4" label="Teams to absorb" />
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* 02 — WORKSTREAMS (8 PILLARS) */}
            <section
                id="workstreams"
                className="bg-[var(--fora-gray)]/40 border-y border-[var(--fora-gray)]"
                data-testid="priorities-section"
            >
                <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-20 lg:py-28">
                    <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
                        <div>
                            <div className="flex items-center gap-3">
                                <span className="red-dot" />
                                <span className="section-num">
                                    02 — Workstreams
                                </span>
                            </div>
                            <h2 className="mt-5 font-[800] tracking-[-0.03em] leading-[1] text-4xl sm:text-5xl">
                                Eight workstreams.
                                <br />
                                Parallel build.
                            </h2>
                        </div>
                        <p className="max-w-md text-[var(--fora-muted)] text-base">
                            Top deliverables per workstream — full work plan
                            tracked in operations sheet.
                        </p>
                    </div>

                    <div
                        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4"
                        data-testid="workstreams-grid"
                    >
                        {WORKSTREAMS.map((w) => (
                            <article
                                key={w.n}
                                className="fora-card flex flex-col"
                                data-testid={`workstream-${w.n}`}
                            >
                                <div className="flex items-baseline justify-between">
                                    <span className="pillar-num text-3xl">
                                        {w.n}
                                    </span>
                                </div>
                                <h3 className="mt-3 font-[700] text-base sm:text-lg tracking-[-0.01em] leading-snug">
                                    {w.name}
                                </h3>
                                <ul className="mt-3 space-y-1.5 flex-1">
                                    {w.items.map((it, i) => (
                                        <li
                                            key={i}
                                            className="flex gap-2 text-xs text-[var(--fora-muted)] leading-snug"
                                        >
                                            <span className="red-dot mt-[6px] shrink-0" />
                                            <span>{it}</span>
                                        </li>
                                    ))}
                                </ul>
                            </article>
                        ))}
                    </div>
                </div>
            </section>

            {/* 03 — FORWARD PLAN (May / June / July) */}
            <section
                id="priorities"
                className="bg-white"
                data-testid="forward-section"
            >
                <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-20 lg:py-28">
                    <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
                        <div>
                            <div className="flex items-center gap-3">
                                <span className="red-dot" />
                                <span className="section-num">
                                    03 — Forward Plan
                                </span>
                            </div>
                            <h2 className="mt-5 font-[800] tracking-[-0.03em] leading-[1] text-4xl sm:text-5xl">
                                May → July 2026.
                                <br />
                                Phased build.
                            </h2>
                        </div>
                        <p className="max-w-md text-[var(--fora-muted)] text-base">
                            Foundation → Activation → Pilot &amp; Scale.
                        </p>
                    </div>

                    <div
                        className="grid grid-cols-1 md:grid-cols-3 gap-4"
                        data-testid="forward-grid"
                    >
                        {FORWARD_PLAN.map((f) => (
                            <div
                                key={f.bucket}
                                className="border border-[var(--fora-ink)] p-5"
                                data-testid={`forward-bucket-${f.bucket
                                    .toLowerCase()
                                    .replace(/[^a-z0-9]+/g, "-")}`}
                            >
                                <div className="text-[10px] font-bold tracking-[0.22em] uppercase text-[var(--fora-red-1)]">
                                    {f.tag}
                                </div>
                                <h3 className="mt-1.5 font-[700] text-lg sm:text-xl tracking-[-0.01em]">
                                    {f.bucket}
                                </h3>
                                <ul className="mt-4 space-y-3">
                                    {f.items.map((it, i) => (
                                        <li key={i}>
                                            <div className="text-sm font-[600] tracking-[-0.01em] leading-snug text-[var(--fora-ink)]">
                                                {it.name}
                                            </div>
                                            <div className="mt-0.5 text-[11px] text-[var(--fora-muted)]">
                                                {it.owner}
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* 04 — KPIs (early state) */}
            <section
                id="kpis"
                className="bg-black text-white relative overflow-hidden"
                data-testid="kpis-section"
            >
                <div className="absolute inset-0 fora-pattern opacity-70" />
                <div className="absolute -top-32 -left-32 w-[420px] h-[420px] rounded-full bg-[var(--fora-red-2)] opacity-20 blur-3xl" />
                <div className="relative max-w-[1280px] mx-auto px-6 lg:px-10 py-20 lg:py-28">
                    <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
                        <div>
                            <div className="flex items-center gap-3">
                                <span className="red-dot" />
                                <span className="section-num text-white/80">
                                    04 — KPI Foundation
                                </span>
                            </div>
                            <h2 className="mt-5 font-[800] tracking-[-0.03em] leading-[1] text-4xl sm:text-5xl">
                                Building the
                                <br />
                                measurement layer.
                            </h2>
                        </div>
                        <p className="max-w-md text-white/55 text-base">
                            KPI tree, scorecards, and dashboards under
                            definition. Most operational KPIs will be live by
                            end of July.
                        </p>
                    </div>

                    <div
                        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
                        data-testid="kpi-foundation-grid"
                    >
                        {[
                            {
                                eyebrow: "Productivity",
                                name: "Productivity & Efficiency KPIs",
                                status: "Definition in progress",
                                target: "May 2026",
                            },
                            {
                                eyebrow: "Service",
                                name: "Support SLAs & response targets",
                                status: "Definition in progress",
                                target: "June 2026",
                            },
                            {
                                eyebrow: "Operational",
                                name: "Operational KPI tree",
                                status: "Aligning with CRO/CTO",
                                target: "June 2026",
                            },
                            {
                                eyebrow: "Reporting",
                                name: "COO Operational Scorecard",
                                status: "Not started",
                                target: "July 2026",
                            },
                            {
                                eyebrow: "Markets",
                                name: "Country-by-country reporting",
                                status: "Not started",
                                target: "July 2026",
                            },
                            {
                                eyebrow: "Executive",
                                name: "COO Executive Dashboard",
                                status: "Not started",
                                target: "July 2026",
                            },
                        ].map((k, i) => (
                            <article
                                key={i}
                                className="border border-white/15 p-5"
                                data-testid={`kpi-foundation-${i}`}
                            >
                                <div className="text-[10px] font-bold tracking-[0.22em] uppercase text-[var(--fora-red-1)]">
                                    {k.eyebrow}
                                </div>
                                <h4 className="mt-2 font-[700] text-base tracking-[-0.01em] leading-snug">
                                    {k.name}
                                </h4>
                                <div className="mt-4 flex items-center justify-between">
                                    <div>
                                        <div className="text-[9px] font-bold tracking-[0.22em] uppercase text-white/45">
                                            Status
                                        </div>
                                        <div className="text-xs text-white/75 mt-0.5">
                                            {k.status}
                                        </div>
                                    </div>
                                    <span className="inline-flex items-center px-2 py-1 border border-[var(--fora-red-1)] text-[var(--fora-red-1)] text-[10px] font-bold tracking-[0.18em] uppercase">
                                        {k.target}
                                    </span>
                                </div>
                            </article>
                        ))}
                    </div>
                </div>
            </section>

            {/* 05 — CAPACITY */}
            <section
                id="capacity"
                className="bg-white border-b border-[var(--fora-gray)]"
                data-testid="capacity-section"
            >
                <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-20 lg:py-28">
                    <div className="grid grid-cols-12 gap-8 items-start">
                        <div className="col-span-12 lg:col-span-5">
                            <div className="flex items-center gap-3">
                                <span className="red-dot" />
                                <span className="section-num">
                                    05 — Capacity
                                </span>
                            </div>
                            <h2 className="mt-5 font-[800] tracking-[-0.03em] leading-[1] text-4xl sm:text-5xl">
                                Two owners.
                                <br />
                                Four teams to absorb.
                            </h2>
                            <p className="mt-6 text-[var(--fora-muted)] text-base leading-relaxed">
                                Operations is currently led by Tanja and Igor
                                Pribek. Existing operational teams transition into the
                                COO function during May–June. Critical
                                capability gaps to be identified during May.
                            </p>
                        </div>
                        <div className="col-span-12 lg:col-span-7">
                            <div
                                className="bg-black text-white p-8 sm:p-10"
                                data-testid="capacity-callout"
                            >
                                <div className="text-[10px] font-bold tracking-[0.22em] uppercase text-[var(--fora-red-1)] mb-4">
                                    Teams to absorb into COO
                                </div>
                                <ul
                                    className="space-y-2"
                                    data-testid="absorb-teams"
                                >
                                    {ABSORB_TEAMS.map((t, i) => (
                                        <li
                                            key={i}
                                            className="flex items-center gap-3 text-base sm:text-lg font-[600] tracking-[-0.01em]"
                                        >
                                            <span className="red-dot shrink-0" />
                                            <span>{t}</span>
                                        </li>
                                    ))}
                                </ul>
                                <div className="mt-6 pt-6 border-t border-white/10 text-sm text-white/70 leading-relaxed">
                                    Hiring plan and capability gap
                                    identification underway. Roles &amp;
                                    responsibilities of COO teams updated to
                                    support International TOM.
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* 06 — DEPENDENCIES (heaviest section) */}
            <section
                id="dependencies"
                className="bg-[var(--fora-gray)]/40 border-b border-[var(--fora-gray)]"
                data-testid="coordination-section"
            >
                <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-20 lg:py-28">
                    <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
                        <div>
                            <div className="flex items-center gap-3">
                                <span className="red-dot" />
                                <span className="section-num">
                                    06 — Dependencies
                                </span>
                            </div>
                            <h2 className="mt-5 font-[800] tracking-[-0.03em] leading-[1] text-4xl sm:text-5xl">
                                Operations doesn&apos;t
                                <br />
                                run alone.
                            </h2>
                        </div>
                        <p className="max-w-md text-[var(--fora-muted)] text-base">
                            Operations execution depends heavily on Product &amp;
                            IT, Finance, and Business teams. Sequencing of
                            joint commitments is the largest execution risk.
                        </p>
                    </div>

                    <div
                        className="grid grid-cols-1 md:grid-cols-3 gap-5"
                        data-testid="dependencies-grid"
                    >
                        {DEPENDENCIES.map((d) => (
                            <div
                                key={d.team}
                                className="border border-[var(--fora-ink)] p-5 flex flex-col"
                                data-testid={`dependency-${d.team
                                    .toLowerCase()
                                    .replace(/[^a-z0-9]+/g, "-")}`}
                            >
                                <div className="flex items-center justify-between mb-3 gap-2">
                                    <div className="text-[10px] font-bold tracking-[0.22em] uppercase text-[var(--fora-red-1)]">
                                        {d.team}
                                    </div>
                                    <div className="flex items-center gap-1.5">
                                        {d.link && <CrossFunctional size="xs" />}
                                        <span className="inline-flex items-center px-2 py-0.5 bg-[var(--fora-ink)] text-white text-[9px] font-bold tracking-[0.18em] uppercase">
                                            {d.weight}
                                        </span>
                                    </div>
                                </div>
                                <div className="text-base font-[700] tracking-[-0.01em]">
                                    {d.owner}
                                </div>
                                <ul className="mt-4 space-y-2 flex-1">
                                    {d.items.map((it, i) => (
                                        <li
                                            key={i}
                                            className="flex gap-2 text-sm text-[var(--fora-ink)]/80 leading-snug"
                                        >
                                            <span className="red-dot mt-[7px] shrink-0" />
                                            <span>{it}</span>
                                        </li>
                                    ))}
                                </ul>
                                {d.link && (
                                    <Link
                                        to={d.link.href}
                                        className="no-print mt-4 pt-3 border-t border-[var(--fora-gray)] inline-flex items-center justify-between gap-2 text-xs font-semibold text-[var(--fora-ink)]/70 hover:text-[var(--fora-red-1)] transition-colors group"
                                        data-testid={`dependency-${d.team.toLowerCase().replace(/[^a-z0-9]+/g, "-")}-link`}
                                    >
                                        <span>↗ {d.link.label}</span>
                                        <ArrowUpRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform shrink-0" />
                                    </Link>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* 07 — RISKS */}
            <section
                id="risks"
                className="bg-white border-b border-[var(--fora-gray)]"
                data-testid="risks-section"
            >
                <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-20 lg:py-28">
                    <div className="grid grid-cols-12 gap-8">
                        <div className="col-span-12 lg:col-span-4">
                            <div className="flex items-center gap-3">
                                <span className="red-dot" />
                                <span className="section-num">
                                    07 — Risks
                                </span>
                            </div>
                            <h2 className="mt-5 font-[800] tracking-[-0.03em] leading-[1] text-4xl sm:text-5xl">
                                Risks &amp;
                                <br />
                                dependencies.
                            </h2>
                        </div>
                        <div className="col-span-12 lg:col-span-8">
                            <div className="text-[10px] font-bold tracking-[0.22em] uppercase text-[var(--fora-red-1)] mb-4">
                                Watch list
                            </div>
                            <ul className="space-y-3">
                                {RISKS.map((r, i) => (
                                    <li
                                        key={i}
                                        className="flex gap-3 text-base leading-relaxed text-[var(--fora-ink)]/85"
                                    >
                                        <span className="red-dot mt-[10px] shrink-0" />
                                        <span>{r}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>
            </section>

            {/* 08 — CLOSING */}
            <section
                id="closing"
                className="bg-[var(--fora-gray)]/40"
                data-testid="closing-section"
            >
                <div className="relative max-w-[1280px] mx-auto px-6 lg:px-10 py-20 lg:py-28">
                    <div className="grid grid-cols-12 gap-8">
                        <div className="col-span-12 lg:col-span-4">
                            <div className="flex items-center gap-3">
                                <span className="red-dot" />
                                <span className="section-num">
                                    08 — Closing
                                </span>
                            </div>
                        </div>
                        <div className="col-span-12 lg:col-span-8">
                            <h2 className="font-[800] tracking-[-0.03em] leading-[1.05] text-2xl sm:text-3xl lg:text-4xl">
                                Operations is in{" "}
                                <span className="bg-[var(--fora-red-1)] text-white px-2">
                                    stand-up mode
                                </span>
                                . May–July is the foundation period.
                            </h2>
                            <p className="mt-6 text-base leading-relaxed text-[var(--fora-ink)]/80">
                                Eight workstreams advance in parallel against
                                a lean ownership model. By end of July, the
                                function should have governance, processes,
                                tooling, and KPIs in place to scale alongside
                                international expansion and Adyen integration.
                            </p>
                            <p className="mt-4 text-base leading-relaxed text-[var(--fora-ink)]/80">
                                The largest executional lever is{" "}
                                <strong>cross-team sequencing</strong> with
                                Product &amp; IT (CTO), Finance (CFO), and
                                Business (CBO) — without coordinated delivery
                                of digital onboarding, asset governance, and
                                market readiness, several workstreams stall.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* 09 — SIGN-OFF */}
            <section
                className="bg-black text-white relative overflow-hidden border-t border-white/10"
                data-testid="signoff-section"
            >
                <div className="absolute inset-0 fora-pattern opacity-80" />
                <div className="relative max-w-[1280px] mx-auto px-6 lg:px-10 py-20 lg:py-24">
                    <div className="grid grid-cols-12 gap-6 md:gap-10">
                        <div className="col-span-12 lg:col-span-5">
                            <div className="flex items-center gap-3">
                                <span className="red-dot" />
                                <span className="section-num text-white/80">
                                    09 — Sign-off
                                </span>
                            </div>
                            <h2 className="mt-5 font-[800] tracking-[-0.03em] leading-[1] text-4xl sm:text-5xl">
                                Prepared by the
                                <br />
                                Operations team.
                            </h2>
                            <p className="mt-6 text-white/70 max-w-md">
                                For questions, deep-dives, or to request a
                                walkthrough of the Operations workplan, reach
                                out below.
                            </p>
                        </div>

                        <div className="col-span-12 lg:col-span-7 grid sm:grid-cols-2 gap-4">
                            <Person
                                name="Tanja Krasić"
                                role="COO · Chief Operating Officer"
                                email="tanja.krasic@master-fb.com"
                            />
                            <Person
                                name="Igor Pribek"
                                role="Operations"
                                email="igor.pribek@master-fb.com"
                            />
                        </div>
                    </div>
                </div>
            </section>
        </ReportLayout>
    );
};

export default OperationsReport;
